import React, {ReactNode, useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import dayjs from "dayjs";
import {Box, Button, Card, Checkbox, Chip, ListItemText, MenuItem, Select, Stack, TextField, Typography} from "@mui/material";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import ManageSearchRoundedIcon from "@mui/icons-material/ManageSearchRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {SearchRestControllerApiFactory, SearchResponseDto, SearchResultDto, SearchResultDtoTypeEnum} from "arteco-api-client-ts";
import {useApiManager} from "../../../components/utils/api";
import {btnSx, EmptyState, Page, PageHeader, Panel, SearchField, SkeletonRows} from "../../../components/kit";
import {dateToString} from "../../../components/utils/dates";
import {SECTION_LABELS, wikiAdminEditPath} from "../../../components/wiki/sections";
import {COMPLIANCE} from "../compliance/access";

/**
 * Buscador de la administración (#14123): registros y documentos de todos los módulos, pero cada uno
 * solo ve lo de los suyos. Busca en todo por defecto; el tipo y la fecha acotan. Qué entra en el
 * índice y con qué rol lo decide el API.
 */

const TYPE_LABELS: Record<SearchResultDtoTypeEnum, string> = {
	PERSON: "Personas",
	PERSON_DOCUMENT: "Documentos de personas",
	TRAINING_RECORD: "Formación",
	CANDIDATE: "Candidatos",
	CANDIDATE_DOCUMENT: "Documentos de candidatos",
	LEVEL_TEST: "Pruebas de nivel",
	LEVEL_SOLUTION: "Soluciones de pruebas",
	RECRUITMENT: "Procesos de selección",
	RECRUITMENT_DOCUMENT: "Documentos de procesos de selección",
	CUSTOMER: "Clientes",
	BILL: "Facturas",
	BUDGET: "Presupuestos",
	SUPPLIER: "Proveedores",
	SUPPLIER_BILL: "Facturas de proveedor",
	ORGANIZATION: "Organizaciones",
	CONTACT: "Contactos",
	LEAD: "Leads",
	ORG_REQUEST: "Oportunidades",
	QUALITY_FINDING: "Hallazgos",
	ACCESS_REVIEW: "Revisiones de accesos",
	COMPANY_DOCUMENT: "Documentos de empresa",
	WIKI_PAGE: "Wiki",
	RECORD_ATTACHMENT: "Documentos anexos",
};

const TYPE_ORDER = Object.keys(TYPE_LABELS) as SearchResultDtoTypeEnum[];

/** Sin filtro de tipo, cuántos se enseñan de cada uno; el resto, con «Ver los N». */
const PER_GROUP = 5;

const KIND_LABELS: Record<string, string> = {SENT: "Enviado", SIGNED: "Firmado", RECEIVED: "Recibido del cliente", OTHER: "Otro"};

/** Atajos de fecha: desde una fecha hasta hoy. */
const DATE_PRESETS: { value: string, label: string, from?: () => string }[] = [
	{value: "any", label: "Cualquier fecha"},
	{value: "30d", label: "Últimos 30 días", from: () => dayjs().subtract(30, "day").format("YYYY-MM-DD")},
	{value: "3m", label: "Últimos 3 meses", from: () => dayjs().subtract(3, "month").format("YYYY-MM-DD")},
	{value: "1y", label: "Último año", from: () => dayjs().subtract(1, "year").format("YYYY-MM-DD")},
	{value: "year", label: "Este año", from: () => dayjs().startOf("year").format("YYYY-MM-DD")},
	{value: "custom", label: "Entre dos fechas…"},
];

/** La ficha a la que lleva un resultado; en un anexo o un documento de persona, la de su registro. */
const pathOf = (r: SearchResultDto): string | undefined => {
	const id = r.targetId;
	switch (r.targetType) {
		case "PERSON": return `/app/admin/employee/person/${id}`;
		case "TRAINING_RECORD": return `/app/admin/employee/training/${id}`;
		case "CANDIDATE": return `/app/admin/recruitment/candidate/${id}`;
		case "LEVEL_TEST": return `/app/admin/recruitment/level/test/${id}`;
		case "RECRUITMENT": return `/app/admin/recruitment/recruitment/${id}`;
		case "CUSTOMER": return `/app/admin/billing/customer/${id}`;
		case "BILL": return `/app/admin/billing/bill/${id}`;
		case "BUDGET": return `/app/admin/billing/budget/${id}`;
		case "SUPPLIER": return `/app/admin/accounting/supplier/${id}`;
		case "SUPPLIER_BILL": return `/app/admin/accounting/supplier-bill/${id}`;
		case "ORGANIZATION": return `/app/admin/marketing/organization/${id}`;
		case "CONTACT": return `/app/admin/marketing/contact/${id}`;
		case "LEAD": return `/app/admin/marketing/lead/${id}`;
		case "ORG_REQUEST": return `/app/admin/marketing/org-request/${id}`;
		case "QUALITY_FINDING": return `${COMPLIANCE}/finding/${id}`;
		case "ACCESS_REVIEW": return `${COMPLIANCE}/access-review/${id}`;
		case "COMPANY_DOCUMENT": return `${COMPLIANCE}/document/${id}`;
		// en la wiki el subtítulo es la sección
		case "WIKI_PAGE": return id ? wikiAdminEditPath(r.subtitle || "", id) : undefined;
		default: return undefined;
	}
};

/** El subtítulo en palabras: la sección de la wiki o el tipo de anexo, en lugar del valor interno. */
const subtitleOf = (r: SearchResultDto): string | undefined => {
	if (r.type === "WIKI_PAGE") return r.subtitle ? SECTION_LABELS[r.subtitle] || r.subtitle : undefined;
	if (r.type === "RECORD_ATTACHMENT" && r.subtitle) {
		const [kind, ...rest] = r.subtitle.split(" · ");
		return [KIND_LABELS[kind] || kind, ...rest].join(" · ");
	}
	return r.subtitle;
};

/** Sin tildes ni mayúsculas, letra a letra: mantiene las posiciones para poder resaltar el original. */
const fold = (s: string) => Array.from(s).map(c => c.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().charAt(0) || c).join("");

/** El trozo con las palabras buscadas en negrita, sin distinguir tildes y por el principio de palabra. */
const highlight = (text: string, query: string): ReactNode => {
	const words = fold(query).split(/[^\p{L}\p{N}]+/u).filter(w => w.length > 0);
	const folded = fold(text);
	const marks: [number, number][] = [];
	for (const w of words) {
		let at = folded.indexOf(w);
		while (at >= 0) {
			if (at === 0 || !/[\p{L}\p{N}]/u.test(folded[at - 1])) marks.push([at, at + w.length]);
			at = folded.indexOf(w, at + w.length);
		}
	}
	if (!marks.length) return text;
	marks.sort((a, b) => a[0] - b[0]);
	const out: ReactNode[] = [];
	let pos = 0;
	marks.forEach(([from, to], i) => {
		if (from < pos) return;
		out.push(text.slice(pos, from), <Box component="mark" key={i} sx={{bgcolor: "transparent", color: "text.primary", fontWeight: 700}}>{text.slice(from, to)}</Box>);
		pos = to;
	});
	out.push(text.slice(pos));
	return out;
};

/** Un resultado en dos líneas: título y subtítulo, y debajo el trozo de texto; la fecha a la derecha. */
const ResultRow = ({r, query, onOpen}: { r: SearchResultDto, query: string, onOpen?: () => void }) => {
	const subtitle = subtitleOf(r);
	return (
		<Box role={onOpen ? "link" : undefined} tabIndex={onOpen ? 0 : undefined} onClick={onOpen}
			 onKeyDown={e => onOpen && e.key === "Enter" && onOpen()}
			 sx={{
				 display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 0.875, cursor: onOpen ? "pointer" : undefined,
				 borderTop: "1px solid", borderColor: "divider", "&:hover": onOpen ? {bgcolor: "grey.50"} : undefined,
				 "&:focus-visible": {outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2},
			 }}>
			<Box sx={{minWidth: 0, flexGrow: 1}}>
				<Typography variant="body2" noWrap>
					<Box component="span" sx={{fontWeight: 600}}>{r.title || "Sin título"}</Box>
					{subtitle && <Box component="span" sx={{color: "text.secondary"}}>{"  ·  " + subtitle}</Box>}
				</Typography>
				{r.snippet && <Typography variant="caption" color="text.secondary" component="div" noWrap>{highlight(r.snippet, query)}</Typography>}
			</Box>
			{r.date && <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0, fontVariantNumeric: "tabular-nums"}}>{dateToString(r.date)}</Typography>}
			{onOpen && <ChevronRightRoundedIcon fontSize="small" sx={{color: "grey.400", flexShrink: 0}}/>}
		</Box>
	);
};

/** Qué atajo de fecha corresponde a los extremos de la URL; si no es ninguno, el rango libre. */
const presetOf = (from: string, to: string): string => {
	if (!from && !to) return "any";
	if (!to) return DATE_PRESETS.find(p => p.from && p.from() === from)?.value || "custom";
	return "custom";
};

const plural = (n: number, one: string, many: string) => n === 1 ? `1 ${one}` : `${n.toLocaleString("es-ES")} ${many}`;

export const SearchPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(SearchRestControllerApiFactory);
	const navigate = useNavigate();
	const [params, setParams] = useSearchParams();
	const [text, setText] = useState(params.get("q") || "");
	// la respuesta con la búsqueda a la que contesta: la de otra búsqueda no se enseña
	const [answer, setAnswer] = useState<{ key: string, response: SearchResponseDto } | undefined>(undefined);
	const query = (params.get("q") || "").trim();
	const types = (params.get("tipos") || "").split(",").filter(t => t in TYPE_LABELS) as SearchResultDtoTypeEnum[];
	const from = params.get("desde") || "";
	const to = params.get("hasta") || "";
	const [customDates, setCustomDates] = useState(presetOf(from, to) === "custom");
	const preset = customDates ? "custom" : presetOf(from, to);

	// la búsqueda y los filtros van en la URL: se puede volver atrás y compartir el enlace
	// sobre la URL más reciente: dos cambios seguidos (desde y hasta) no se pisan
	const update = (changes: Record<string, string | undefined>) => {
		setParams(prev => {
			const next = new URLSearchParams(prev);
			Object.entries(changes).forEach(([k, v]) => v ? next.set(k, v) : next.delete(k));
			return next;
		}, {replace: true});
	};

	useEffect(() => {
		const t = setTimeout(() => {
			if (text.trim() !== query) update({q: text.trim()});
		}, 300);
		return () => clearTimeout(t);
	}, [text]);

	const filterKey = [query, types.join(","), from, to].join("|");
	const response = answer?.key === filterKey ? answer.response : undefined;
	useEffect(() => {
		if (!query) {
			setAnswer(undefined);
			return;
		}
		// una respuesta que llega tarde, de una búsqueda ya cambiada, no pisa a la vigente
		let current = true;
		// sin filtro de tipo, los mejores de cada uno; con filtro, los mejores de los elegidos
		apiManager.execute(ctrl.search(query, types.length ? types : undefined, from || undefined, to || undefined,
									   types.length ? undefined : PER_GROUP))
			.then(r => current && setAnswer({key: filterKey, response: r || {results: [], counts: {}}}))
			.catch(() => current && setAnswer({key: filterKey, response: {results: [], counts: {}}}));
		return () => {
			current = false;
		};
	}, [filterKey]);

	const counts = (response?.counts || {}) as Record<string, number>;
	const results = response?.results || [];
	const total = Object.entries(counts).filter(([t]) => !types.length || types.includes(t as SearchResultDtoTypeEnum))
		.reduce((sum, [, n]) => sum + n, 0);
	// en el selector, los tipos con resultados y los ya elegidos, aunque ahora no tengan
	const typeOptions = TYPE_ORDER.filter(t => counts[t] || types.includes(t));
	const groups = TYPE_ORDER
		.map(type => ({type, items: results.filter(r => r.type === type)}))
		.filter(g => g.items.length > 0);
	const filtered = types.length > 0 || !!from || !!to;

	const setPreset = (value: string) => {
		if (value === "custom") {
			setCustomDates(true);
			return;
		}
		setCustomDates(false);
		update({desde: DATE_PRESETS.find(p => p.value === value)?.from?.(), hasta: undefined});
	};

	const filters = (
		<Stack direction={{xs: "column", md: "row"}} spacing={1.5} alignItems={{md: "center"}} sx={{mb: 2}} useFlexGap flexWrap="wrap">
			<Box sx={{flexGrow: 1, minWidth: {md: 280}, "& .MuiTextField-root": {width: "100%"}}}>
				<SearchField value={text} onChange={setText} placeholder="Nombre, número, palabra de un documento…" width={420}/>
			</Box>
			<Select size="small" multiple displayEmpty value={types} sx={{minWidth: 220, borderRadius: 2}}
					inputProps={{"aria-label": "Tipo"}}
					onChange={e => update({tipos: (e.target.value as string[]).join(",")})}
					renderValue={sel => sel.length === 0 ? "Todos los tipos"
						: sel.length === 1 ? TYPE_LABELS[sel[0] as SearchResultDtoTypeEnum] : `${sel.length} tipos`}
					MenuProps={{PaperProps: {sx: {maxHeight: 420}}}}>
				{typeOptions.length === 0 && <MenuItem disabled>Busca algo para ver los tipos</MenuItem>}
				{typeOptions.map(t => (
					<MenuItem key={t} value={t} dense>
						<Checkbox size="small" checked={types.includes(t)} sx={{p: 0.5, mr: 1}}/>
						<ListItemText primary={TYPE_LABELS[t]}/>
						<Typography variant="caption" color="text.secondary" sx={{ml: 2}}>{counts[t] || 0}</Typography>
					</MenuItem>
				))}
			</Select>
			<Select size="small" value={preset} sx={{minWidth: 190, borderRadius: 2}} inputProps={{"aria-label": "Fecha"}}
					onChange={e => setPreset(e.target.value as string)}>
				{DATE_PRESETS.map(p => <MenuItem key={p.value} value={p.value} dense>{p.label}</MenuItem>)}
			</Select>
			{preset === "custom" && (
				<Stack direction="row" spacing={1}>
					<TextField size="small" type="date" label="Desde" value={from} InputLabelProps={{shrink: true}}
							   onChange={e => update({desde: e.target.value})} sx={{width: 160}}/>
					<TextField size="small" type="date" label="Hasta" value={to} InputLabelProps={{shrink: true}}
							   onChange={e => update({hasta: e.target.value})} sx={{width: 160}}/>
				</Stack>
			)}
			{filtered && (
				<Button size="small" sx={btnSx} onClick={() => {
					setCustomDates(false);
					update({tipos: undefined, desde: undefined, hasta: undefined});
				}}>Quitar filtros</Button>
			)}
		</Stack>
	);

	let body;
	if (!query) {
		body = <Panel><EmptyState icon={<ManageSearchRoundedIcon/>} title="Busca en la Extranet"
								  description="Personas, candidatos, clientes, facturas, proveedores, organizaciones, la wiki y el texto de los documentos: anexos, CV, facturas de proveedor, documentos de empresa… Solo aparece lo de los módulos a los que tienes acceso."/></Panel>;
	} else if (response === undefined) {
		body = <Panel><SkeletonRows rows={6} height={40}/></Panel>;
	} else if (groups.length === 0) {
		body = <Panel><EmptyState icon={<SearchOffRoundedIcon/>} title={<>Sin resultados para «{query}»</>}
								  description={filtered ? "Prueba a quitar los filtros de tipo o de fecha." : "Prueba con otra palabra o con el principio de una. Solo se busca en los módulos a los que tienes acceso."}/></Panel>;
	} else {
		body = (
			<Stack spacing={2}>
				{groups.map(g => {
					const count = counts[g.type] || g.items.length;
					const shown = g.items;
					return (
						<Card key={g.type} elevation={0} sx={{borderRadius: 2.5, border: "1px solid", borderColor: "divider"}}>
							<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{px: 2, py: 0.75, bgcolor: "grey.50"}}>
								<Typography variant="subtitle2" component="h2" fontWeight={700}>
									{TYPE_LABELS[g.type]} <Box component="span" sx={{color: "text.secondary", fontWeight: 500}}>{count}</Box>
								</Typography>
								{count > shown.length && !types.length && (
									<Button size="small" sx={{...btnSx, py: 0}} onClick={() => update({tipos: g.type})}>
										Ver {count === 1 ? "el" : "los"} {count}
									</Button>
								)}
							</Stack>
							{shown.map(r => {
								const path = pathOf(r);
								return <ResultRow key={r.type + "-" + r.id} r={r} query={query} onOpen={path ? () => navigate(path) : undefined}/>;
							})}
						</Card>
					);
				})}
			</Stack>
		);
	}

	return (
		<Page>
			<PageHeader title="Buscar"
						subtitle={query && response
							? plural(total, "resultado", "resultados") + (types.length && results.length < total ? ` · se muestran los ${results.length} más relevantes` : "")
							: "En todos tus módulos"}/>
			{filters}
			{types.length > 1 && (
				<Stack direction="row" spacing={1} sx={{mb: 2, flexWrap: "wrap"}} useFlexGap>
					{types.map(t => <Chip key={t} size="small" label={TYPE_LABELS[t]}
										  onDelete={() => update({tipos: types.filter(x => x !== t).join(",")})}/>)}
				</Stack>
			)}
			{body}
		</Page>
	);
};
